import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { errorMessage } from '../api/client';
import type { VersionSuggestionAction } from '../api/versions';
import { cn } from '../lib/utils';
import {
  fileLabel,
  outcomeSummary,
  sortStrongFirst,
  suggestionStrength,
  suggestionWhyText,
  withoutIds,
  type ReviewableSuggestion,
  type SuggestionOutcome,
} from '../lib/version-suggestions';
import { Button } from './button';
import { Card } from './card';
import { ConfirmDialog } from './confirm-dialog';
import { Chip } from './status-chip';
import { useToast } from './toast';

// "Possible new version of …" (ADR-0031) — ONE compact list for the admin room
// and the seller portal (owner ask 2026-10-15). Each app owns its react-query
// hooks and passes the data and the write in; the API decides what each may see
// and do (`canDecide`).

/** Rows per page; past this the list pages rather than growing the page. */
export const SUGGESTIONS_PAGE_SIZE = 25;

/** Decides suggestions on the server: the app's mutation (bulk when it can, else one by one). */
export type ResolveSuggestions = (input: {
  ids: readonly string[];
  action: VersionSuggestionAction;
}) => Promise<SuggestionOutcome[]>;

const pairLabel = (s: ReviewableSuggestion) =>
  `${fileLabel(s.documentName)} ⟶ ${fileLabel(s.existingDocumentName)}`;

interface DecisionError {
  message: string;
  /** "new ⟶ existing", for when the suggestion is gone from the list by the time it failed. */
  label: string;
}

/**
 * Deciding suggestions — one or many — for a list: which are in flight, which
 * failed (and why, per row), which are done (hidden at once, before the re-read
 * lands), and a toast that says what happened.
 */
export function useSuggestionDecisions(resolve: ResolveSuggestions) {
  const toast = useToast();
  const [done, setDone] = useState<ReadonlySet<string>>(() => new Set());
  const [errors, setErrors] = useState<ReadonlyMap<string, DecisionError>>(() => new Map());
  const [pending, setPending] = useState<{
    ids: ReadonlySet<string>;
    action: VersionSuggestionAction;
  } | null>(null);

  const run = async (
    suggestions: readonly ReviewableSuggestion[],
    action: VersionSuggestionAction,
  ): Promise<SuggestionOutcome[]> => {
    if (suggestions.length === 0 || pending) return [];
    const ids = suggestions.map((s) => s.id);
    const labels = new Map(suggestions.map((s) => [s.id, pairLabel(s)]));
    const fail = (next: Map<string, DecisionError>, id: string, message: string) =>
      next.set(id, { message, label: labels.get(id) ?? '' });
    setPending({ ids: new Set(ids), action });
    setErrors((prev) => withoutIds(prev, ids));
    try {
      const outcomes = await resolve({ ids, action });
      setDone((prev) => new Set([...prev, ...outcomes.filter((o) => o.ok).map((o) => o.id)]));
      setErrors((prev) => {
        const next = new Map(prev);
        outcomes.forEach((o) => {
          if (!o.ok) fail(next, o.id, o.error ?? 'Not saved. Try again.');
        });
        return next;
      });
      toast({ message: outcomeSummary(outcomes, action) });
      return outcomes;
    } catch (err) {
      // resolveVersionSuggestions answers per suggestion; this is a last resort.
      const message = errorMessage(err) ?? 'Not saved. Try again.';
      setErrors((prev) => {
        const next = new Map(prev);
        ids.forEach((id) => fail(next, id, message));
        return next;
      });
      return [];
    } finally {
      setPending(null);
    }
  };
  return { run, done, errors, pending };
}

export type SuggestionDecisions = ReturnType<typeof useSuggestionDecisions>;

interface RowProps {
  suggestion: ReviewableSuggestion;
  decisions: SuggestionDecisions;
  /** Open a document (the app's viewer or details panel) so the two can be compared. */
  onOpenDocument?: (documentId: string) => void;
  /** Present on the room-wide list: the row's checkbox. */
  selection?: { checked: boolean; onChange: (checked: boolean) => void };
}

/**
 * One suggestion on one line: new file ⟶ existing file, a Strong / Possible chip
 * when the server says, the reason in small text, and small Merge / Keep
 * separate buttons — or, when the caller may not decide it, who does. Stacks
 * under 640px.
 */
export function VersionSuggestionRow({
  suggestion: s,
  decisions,
  onOpenDocument,
  selection,
}: RowProps) {
  const strength = suggestionStrength(s);
  const error = decisions.errors.get(s.id)?.message;
  const busy = decisions.pending !== null;
  const mine = decisions.pending?.ids.has(s.id) ? decisions.pending.action : null;
  const newName = fileLabel(s.documentName);
  const oldName = fileLabel(s.existingDocumentName);
  const name = (documentId: string, label: string, role: string) =>
    onOpenDocument ? (
      <button
        type="button"
        className="min-w-0 break-words text-left font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
        onClick={() => onOpenDocument(documentId)}
        aria-label={`Open ${role} file ${label}`}
      >
        {label}
      </button>
    ) : (
      <span className="min-w-0 break-words font-semibold">{label}</span>
    );

  return (
    <li className="flex items-start gap-3 border-b border-line py-2.5 last:border-b-0">
      {selection ? (
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 shrink-0 max-md:mt-0.5 max-md:h-5 max-md:w-5"
          checked={selection.checked}
          disabled={!s.canDecide || busy}
          onChange={(e) => selection.onChange(e.target.checked)}
          aria-label={`Select ${newName}, a possible new version of ${oldName}`}
        />
      ) : null}
      <div className="min-w-0 flex-1 sm:flex sm:items-start sm:gap-3">
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm">
            {name(s.documentId, newName, 'new')}
            <span aria-hidden="true" className="text-ink-2">
              ⟶
            </span>
            <span className="sr-only">may be a new version of</span>
            {name(s.existingDocumentId, oldName, 'existing')}
            {strength === 'strong' ? (
              <Chip tone="classified" className="ml-1">
                Strong
              </Chip>
            ) : strength === 'possible' ? (
              <Chip tone="neutral" className="ml-1">
                Possible
              </Chip>
            ) : null}
          </p>
          <p className="mt-0.5 text-xs text-ink-2">{suggestionWhyText(s)}</p>
          {error ? (
            <p className="mt-1 text-xs text-destructive" role="alert">
              {error}
            </p>
          ) : null}
        </div>
        {s.canDecide ? (
          <div className="mt-2 flex shrink-0 flex-wrap gap-2 sm:mt-0">
            <Button
              size="sm"
              onClick={() => void decisions.run([s], 'merge')}
              disabled={busy}
              aria-label={`Merge ${newName} as a new version of ${oldName}`}
            >
              {mine === 'merge' ? 'Merging…' : 'Merge'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void decisions.run([s], 'keep-separate')}
              disabled={busy}
              aria-label={`Keep ${newName} separate from ${oldName}`}
            >
              {mine === 'keep-separate' ? 'Saving…' : 'Keep separate'}
            </Button>
          </div>
        ) : (
          <p className="mt-2 text-xs text-ink-2 sm:mt-0">
            The room administrator decides this one.
          </p>
        )}
      </div>
    </li>
  );
}

/**
 * Errors for suggestions that are no longer in the list (e.g. the server says a
 * merge no longer applies and drops it): said here, so the reason is not lost
 * with its row.
 */
export function OrphanDecisionErrors({
  decisions,
  listedIds,
}: {
  decisions: SuggestionDecisions;
  listedIds: ReadonlySet<string>;
}) {
  const orphans = [...decisions.errors].filter(([id]) => !listedIds.has(id));
  if (orphans.length === 0) return null;
  return (
    <ul className="space-y-1" role="alert">
      {orphans.map(([id, e]) => (
        <li key={id} className="text-sm text-destructive">
          {e.label ? `${e.label}: ` : ''}
          {e.message}
        </li>
      ))}
    </ul>
  );
}

interface ReviewCardProps {
  /** The room's open suggestions (undefined while loading). */
  suggestions: readonly ReviewableSuggestion[] | undefined;
  decisions: SuggestionDecisions;
  /** Open a document (the app's viewer or details panel) so the two files can be compared. */
  onOpenDocument?: (documentId: string) => void;
  /** Start expanded (tests and the harness); the pages start collapsed. */
  defaultExpanded?: boolean;
  /** A "?" help tip shown after the card's title line. */
  titleAside?: ReactNode;
  /** A "?" help tip shown after the line explaining Merge and Keep separate (opened list). */
  mergeAside?: ReactNode;
}

interface PendingConfirm {
  items: ReviewableSuggestion[];
  action: VersionSuggestionAction;
}

/**
 * The room's "Possible new versions" as a one-line summary — "34 possible new
 * versions · Review" — that opens a compact list: one row per pair, strong
 * matches first, a checkbox per row the caller may decide, Select all, "Merge
 * selected" / "Keep selected separate" (confirmed with the count), and "Merge
 * all N strong matches". Bulk actions only ever cover rows with `canDecide`.
 * 25 rows a page. Decided rows disappear and a toast says what happened; a row
 * that was not saved says why on the row. Renders nothing when there is none.
 */
export function VersionSuggestionsReviewCard({
  suggestions,
  decisions,
  onOpenDocument,
  defaultExpanded = false,
  titleAside,
  mergeAside,
}: ReviewCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [page, setPage] = useState(0);
  const [confirm, setConfirm] = useState<PendingConfirm | null>(null);
  const listId = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);

  const all = useMemo(
    () => sortStrongFirst((suggestions ?? []).filter((s) => !decisions.done.has(s.id))),
    [suggestions, decisions.done],
  );
  const decidable = all.filter((s) => s.canDecide);
  const strong = decidable.filter((s) => suggestionStrength(s) === 'strong');
  const chosen = decidable.filter((s) => selected.has(s.id));
  const pageCount = Math.max(1, Math.ceil(all.length / SUGGESTIONS_PAGE_SIZE));
  const current = Math.min(page, pageCount - 1);
  const rows = all.slice(current * SUGGESTIONS_PAGE_SIZE, (current + 1) * SUGGESTIONS_PAGE_SIZE);
  const busy = decisions.pending !== null;
  const allSelected = decidable.length > 0 && chosen.length === decidable.length;
  const someSelected = chosen.length > 0 && !allSelected;

  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected;
  }, [someSelected, expanded]);

  const listedIds = new Set(all.map((s) => s.id));
  const orphanErrors = [...decisions.errors.keys()].some((id) => !listedIds.has(id));
  if (all.length === 0 && !orphanErrors) return null;

  const count = all.length;
  // Owner, 2026-10-22: "2 possible new versions · 1 strong" did not say what it
  // was or what to do. Say what these uploads are, then what to do about them.
  const title =
    count === 0
      ? 'Possible new versions'
      : count === 1
        ? '1 upload looks like a newer version of a document already in the room'
        : `${count} uploads look like newer versions of documents already in the room`;
  const advice =
    count === 1
      ? 'Review it to make it the current version, or keep it separate.'
      : 'Review them to make each the current version, or keep them separate.';

  const execute = async ({ items, action }: PendingConfirm) => {
    setConfirm(null);
    const outcomes = await decisions.run(items, action);
    const ok = outcomes.filter((o) => o.ok).map((o) => o.id);
    setSelected((prev) => withoutIds(prev, ok));
    // The buttons that had focus may be gone with their rows: keep the person in the list.
    const active = document.activeElement;
    if (!active || active === document.body) listRef.current?.focus();
  };

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-3">
        <div className="min-w-0 text-sm">
          <div className="flex items-center gap-1.5">
            <p className="font-semibold">{title}</p>
            {titleAside}
          </div>
          {count > 0 ? (
            <p className="text-ink-2">
              {advice}
              {strong.length > 0
                ? ` ${strong.length} ${strong.length === 1 ? 'is a strong match' : 'are strong matches'}.`
                : ''}
            </p>
          ) : null}
        </div>
        {count > 0 ? (
          <Button
            size="sm"
            variant="outline"
            aria-expanded={expanded}
            aria-controls={listId}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? 'Hide' : 'Review'}
          </Button>
        ) : null}
      </div>
      {orphanErrors ? (
        <div className="px-4 pb-3">
          <OrphanDecisionErrors decisions={decisions} listedIds={listedIds} />
        </div>
      ) : null}

      {expanded && count > 0 ? (
        <div
          id={listId}
          ref={listRef}
          tabIndex={-1}
          aria-label="Possible new versions"
          role="region"
          className="space-y-3 border-t border-line px-4 pb-4 pt-3 focus:outline-none"
        >
          <div className="flex items-center gap-1.5">
            <p className="text-xs text-ink-2">
              Merging makes the new file the current version; the older one stays in its version
              history. Click a file name to look at it.
            </p>
            {mergeAside}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label className="mr-1 flex items-center gap-2 text-sm font-medium">
              <input
                ref={selectAllRef}
                type="checkbox"
                className="h-4 w-4 max-md:h-5 max-md:w-5"
                checked={allSelected}
                disabled={busy || decidable.length === 0}
                onChange={(e) =>
                  setSelected(e.target.checked ? new Set(decidable.map((s) => s.id)) : new Set())
                }
              />
              Select all ({decidable.length})
            </label>
            <Button
              size="sm"
              disabled={busy || chosen.length === 0}
              onClick={() => setConfirm({ items: chosen, action: 'merge' })}
            >
              Merge selected{chosen.length > 0 ? ` (${chosen.length})` : ''}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy || chosen.length === 0}
              onClick={() => setConfirm({ items: chosen, action: 'keep-separate' })}
            >
              Keep selected separate{chosen.length > 0 ? ` (${chosen.length})` : ''}
            </Button>
            {strong.length > 0 ? (
              <Button
                size="sm"
                variant="secondary"
                className="sm:ml-auto"
                disabled={busy}
                onClick={() => setConfirm({ items: strong, action: 'merge' })}
              >
                Merge all {strong.length} strong {strong.length === 1 ? 'match' : 'matches'}
              </Button>
            ) : null}
          </div>

          <ul aria-label="Possible new versions, strong matches first">
            {rows.map((s) => (
              <VersionSuggestionRow
                key={s.id}
                suggestion={s}
                decisions={decisions}
                onOpenDocument={onOpenDocument}
                selection={{
                  checked: selected.has(s.id),
                  onChange: (checked) =>
                    setSelected((prev) =>
                      checked ? new Set([...prev, s.id]) : withoutIds(prev, [s.id]),
                    ),
                }}
              />
            ))}
          </ul>

          {pageCount > 1 ? (
            <nav
              aria-label="Pages of possible new versions"
              className="flex flex-wrap items-center justify-between gap-2 text-sm"
            >
              <span className="text-ink-2" aria-live="polite">
                {current * SUGGESTIONS_PAGE_SIZE + 1}–
                {Math.min((current + 1) * SUGGESTIONS_PAGE_SIZE, count)} of {count}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={current === 0}
                  onClick={() => setPage(current - 1)}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={current >= pageCount - 1}
                  onClick={() => setPage(current + 1)}
                >
                  Next
                </Button>
              </div>
            </nav>
          ) : null}
        </div>
      ) : null}

      {confirm ? (
        <ConfirmDialog
          title={
            confirm.action === 'merge'
              ? `Merge ${confirm.items.length} as new versions?`
              : `Keep ${confirm.items.length} separate?`
          }
          confirmLabel={
            confirm.action === 'merge'
              ? `Merge ${confirm.items.length}`
              : `Keep ${confirm.items.length} separate`
          }
          onConfirm={() => void execute(confirm)}
          onCancel={() => setConfirm(null)}
        >
          <p>
            {confirm.action === 'merge'
              ? 'Each new file becomes the current version of the file it matches. The older file stays in its version history, and a merge can be split back out later.'
              : 'Each pair stays as two documents and won’t be suggested again.'}
          </p>
        </ConfirmDialog>
      ) : null}
    </Card>
  );
}

interface DocumentSuggestionsProps {
  /** The room's open suggestions (undefined while loading). */
  suggestions: readonly ReviewableSuggestion[] | undefined;
  /** The document whose panel this sits in: only its suggestions show. */
  documentId: string;
  decisions: SuggestionDecisions;
  /** Open a document (the app's viewer or details panel). */
  onOpenDocument?: (documentId: string) => void;
  className?: string;
}

/**
 * The suggestions involving ONE document, in its details / review panel: the
 * same compact rows as the room-wide list, with Merge / Keep separate. Renders
 * nothing while loading or when there is none.
 */
export function DocumentVersionSuggestions({
  suggestions,
  documentId,
  decisions,
  onOpenDocument,
  className,
}: DocumentSuggestionsProps) {
  const shown = sortStrongFirst(
    (suggestions ?? []).filter(
      (s) =>
        !decisions.done.has(s.id) &&
        (s.documentId === documentId || s.existingDocumentId === documentId),
    ),
  );
  const listedIds = new Set(shown.map((s) => s.id));
  if (shown.length === 0 && decisions.errors.size === 0) return null;
  return (
    <section aria-label="Possible new versions" className={cn('space-y-1', className)}>
      {shown.length > 0 ? (
        <>
          <h3 className="text-sm font-semibold">
            {shown.length === 1
              ? 'A possible new version'
              : `Possible new versions (${shown.length})`}
          </h3>
          <ul>
            {shown.map((s) => (
              <VersionSuggestionRow
                key={s.id}
                suggestion={s}
                decisions={decisions}
                onOpenDocument={onOpenDocument}
              />
            ))}
          </ul>
        </>
      ) : null}
      <OrphanDecisionErrors decisions={decisions} listedIds={listedIds} />
    </section>
  );
}
